import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Copy, Archive, Trash2, Save, FileText, Mail, MessageSquare, Monitor, Database, Eye, X } from "lucide-react";

const TYPE_ICON = { email: Mail, sms: MessageSquare, document: FileText, portal: Monitor };

export default function Templates() {
  const { user } = useAuth();
  const [all, setAll] = useState([]);
  const [options, setOptions] = useState({ categories: [], types: [], placeholders: {} });
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [typeFilter, setTypeFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [preview, setPreview] = useState(null);

  async function load() {
    const r = await api.get("/templates", { params: { include_archived: showArchived } });
    setAll(r.data);
  }
  useEffect(() => { api.get("/templates/options").then(r => setOptions(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  async function seed() {
    if (!window.confirm("Import Bright Manager templates? Existing templates with the same name+category are skipped.")) return;
    try {
      const r = await api.post("/templates/seed");
      toast.success(`Imported ${r.data.inserted} templates (${r.data.skipped} already existed)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
  }

  const filtered = useMemo(() => {
    return all.filter(t => {
      if (activeCategory !== "All" && t.category !== activeCategory) return false;
      if (typeFilter && t.type !== typeFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        return (t.name || "").toLowerCase().includes(q) ||
               (t.body || "").toLowerCase().includes(q) ||
               (t.subject || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [all, activeCategory, typeFilter, query]);

  const categoryCounts = useMemo(() => {
    const c = {};
    all.forEach(t => { c[t.category] = (c[t.category] || 0) + 1; });
    return c;
  }, [all]);

  async function save(tpl) {
    try {
      if (tpl.id) {
        const r = await api.patch(`/templates/${tpl.id}`, tpl);
        setSelected(r.data);
      } else {
        const r = await api.post("/templates", tpl);
        setSelected(r.data);
      }
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  }

  async function duplicate(t) {
    const r = await api.post(`/templates/${t.id}/duplicate`);
    toast.success("Duplicated");
    setSelected(r.data);
    load();
  }

  async function toggleArchive(t) {
    const r = await api.patch(`/templates/${t.id}`, { archived: !t.archived });
    toast.success(r.data.archived ? "Archived" : "Restored");
    setSelected(r.data);
    load();
  }

  async function remove(t) {
    if (!window.confirm(`Permanently delete "${t.name}"? This cannot be undone.`)) return;
    await api.delete(`/templates/${t.id}`);
    setSelected(null);
    load();
  }

  async function previewTemplate(t) {
    // Build sample context from placeholder dictionary (first word of description works as a sample).
    const samples = {
      firstname: "Jane", name: "ABC Ltd", clientname: "Jane Doe", email: "jane@abc.co.uk",
      companynumber: "12345678", signoff: "Kind regards,\nSarah Whitfield\nFinchley Business Services",
      documentname: "2025 Accounts.pdf", formname: "Client Onboarding Form", taskname: "Prepare Accounts",
      subject: "Your query", date: new Date().toLocaleDateString("en-GB"),
      accountsyearend: "31 March 2026", accountsstartdate: "01 April 2025",
      serviceslist: "Statutory Accounts, Corporation Tax, VAT Returns, Payroll",
      missingrecords: "- Bank statements for Q4\n- Purchase invoices for March",
      missingdetails: "- UTR number\n- Director's address",
      expectedcodes: "SA12345678, CT12345678",
      latestactiondate: "15 Jan 2026",
      cs01data: "Director: Jane Doe\nShareholders: Jane Doe (100%)",
      confirmationstatementdate: "31 March 2026", confirmationstatementdue: "14 April 2026",
      sataxamountduejan: "4,250.00", sataxamountduejul: "2,125.00",
      sataxoffice: "Cardiff", sataxreference: "AB 12 34 56 C", sataxyear: "2025/26",
      hmrcaccountno: "12001039",
      ctamountdue: "8,500.00", cttaxdate: "31 December 2026",
      taxoffice: "Shipley", taxreference: "1234567890",
      vatbillamount: "3,750.00", vatbilldue: "07 Feb 2026", vatnumber: "GB 123 4567 89",
      mtdquarterlyperiod: "01 Oct 2025 - 31 Dec 2025",
    };
    try {
      const r = await api.post(`/templates/${t.id}/render`, { context: samples });
      setPreview(r.data);
    } catch (e) { toast.error("Preview failed"); }
  }

  return (
    <div className="flex h-[calc(100vh-0px)] bg-[var(--bg)]">
      {/* Left: category tree */}
      <aside className="w-56 border-r border-[var(--line)] bg-[var(--surface)] flex flex-col shrink-0">
        <div className="p-4 border-b border-[var(--line)]">
          <h1 className="font-serif-strong text-lg">Templates</h1>
          <p className="text-[11px] text-[var(--ink-2)]">{all.length} total</p>
        </div>
        <div className="p-3 border-b border-[var(--line)]">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
            <input
              data-testid="tpl-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates…"
              className="input text-xs w-full pl-6"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <CategoryItem label="All" count={all.length} active={activeCategory === "All"} onClick={() => setActiveCategory("All")} />
          {options.categories.map(c => (
            <CategoryItem key={c} label={c} count={categoryCounts[c] || 0} active={activeCategory === c} onClick={() => setActiveCategory(c)} />
          ))}
        </div>
        <div className="p-3 border-t border-[var(--line)] space-y-2">
          <label className="flex items-center gap-2 text-[11px] text-[var(--ink-2)] cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
          </label>
          {user?.role === "admin" && (
            <button data-testid="tpl-seed" onClick={seed} className="btn-ghost w-full flex items-center justify-center gap-1.5 text-xs !py-1.5">
              <Database size={11} /> Import Bright Manager pack
            </button>
          )}
          <button data-testid="tpl-new" onClick={() => setShowNew(true)} className="btn-primary w-full flex items-center justify-center gap-1.5 text-xs !py-1.5">
            <Plus size={11} /> New template
          </button>
        </div>
      </aside>

      {/* Middle: list */}
      <section className="w-80 border-r border-[var(--line)] bg-white flex flex-col shrink-0">
        <div className="p-3 border-b border-[var(--line)] flex items-center justify-between">
          <div className="text-xs text-[var(--ink-2)]">
            {activeCategory} — <strong>{filtered.length}</strong>
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="tpl-type-filter"
            className="text-[10px] px-1.5 py-0.5 border border-[var(--line)] rounded bg-white"
          >
            <option value="">All types</option>
            {options.types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map(t => {
            const Icon = TYPE_ICON[t.type] || Mail;
            const isActive = selected?.id === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tpl-row-${t.id}`}
                onClick={() => setSelected(t)}
                className={`w-full text-left px-4 py-2.5 border-b border-[var(--line)] transition flex items-start gap-2 ${isActive ? "bg-[var(--navy)]/5" : "hover:bg-[var(--bg-2)]"}`}
              >
                <Icon size={13} className="mt-0.5 text-[var(--ink-2)] shrink-0" strokeWidth={1.5} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{t.name}{t.archived && <span className="text-[9px] uppercase text-[var(--ink-2)] ml-2">archived</span>}</div>
                  <div className="text-[11px] text-[var(--ink-2)] line-clamp-1">{(t.body || "").slice(0, 70)}</div>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center text-xs text-[var(--ink-2)] py-10">
              No templates in this view.
            </div>
          )}
        </div>
      </section>

      {/* Right: editor */}
      <section className="flex-1 overflow-y-auto">
        {selected ? (
          <TemplateEditor
            key={selected.id}
            tpl={selected}
            options={options}
            onSave={save}
            onDuplicate={() => duplicate(selected)}
            onArchive={() => toggleArchive(selected)}
            onDelete={() => remove(selected)}
            onPreview={() => previewTemplate(selected)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-[var(--ink-2)] p-8">
            <FileText size={48} strokeWidth={1} className="opacity-30 mb-3" />
            <div className="text-sm">Pick a template to edit, or create a new one.</div>
          </div>
        )}
      </section>

      {showNew && (
        <TemplateEditor
          isNew
          options={options}
          tpl={{ name: "", category: options.categories[0] || "Other", type: "email", subject: "", body: "" }}
          onSave={async (t) => { await save(t); setShowNew(false); }}
          onCancel={() => setShowNew(false)}
          floating
        />
      )}

      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function CategoryItem({ label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-1.5 text-xs flex items-center justify-between transition ${active ? "bg-[var(--navy)]/10 text-[var(--navy)] font-medium" : "hover:bg-[var(--bg-2)] text-[var(--ink)]"}`}
    >
      <span className="truncate">{label}</span>
      <span className="text-[10px] text-[var(--ink-2)]">{count}</span>
    </button>
  );
}

function TemplateEditor({ tpl, options, onSave, onDuplicate, onArchive, onDelete, onPreview, onCancel, isNew = false, floating = false }) {
  const [form, setForm] = useState(tpl);
  const bodyRef = useRef(null);
  useEffect(() => setForm(tpl), [tpl]);

  function insertPlaceholder(ph) {
    const el = bodyRef.current;
    if (!el) {
      setForm(f => ({ ...f, body: (f.body || "") + ph }));
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const next = (form.body || "").slice(0, start) + ph + (form.body || "").slice(end);
    setForm(f => ({ ...f, body: next }));
    setTimeout(() => { el.focus(); el.setSelectionRange(start + ph.length, start + ph.length); }, 0);
  }

  const content = (
    <div className="p-5 space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <input
            data-testid="editor-name"
            value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Template name"
            className="input text-lg font-medium w-full"
          />
          <div className="flex items-center gap-2 mt-2">
            <select
              data-testid="editor-category"
              value={form.category}
              onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
              className="text-xs px-2 py-1 border border-[var(--line)] rounded bg-white"
            >
              {options.categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              data-testid="editor-type"
              value={form.type}
              onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
              className="text-xs px-2 py-1 border border-[var(--line)] rounded bg-white"
            >
              {options.types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {form.archived && <span className="text-[10px] uppercase bg-[var(--ink-2)]/10 px-1.5 py-0.5 rounded">Archived</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isNew && (
            <>
              <button data-testid="editor-preview" onClick={onPreview} title="Preview with sample data" className="btn-ghost !px-2 !py-1.5 text-xs"><Eye size={13} /></button>
              <button onClick={onDuplicate} title="Duplicate" className="btn-ghost !px-2 !py-1.5 text-xs"><Copy size={13} /></button>
              <button onClick={onArchive} title={form.archived ? "Restore" : "Archive"} className="btn-ghost !px-2 !py-1.5 text-xs"><Archive size={13} /></button>
              <button onClick={onDelete} title="Delete" className="!px-2 !py-1.5 text-xs rounded border border-[var(--err)] text-[var(--err)] hover:bg-[var(--err-bg)]"><Trash2 size={13} /></button>
            </>
          )}
        </div>
      </div>

      {form.type === "email" && (
        <div>
          <label className="text-xs text-[var(--ink-2)] block mb-1">Subject</label>
          <input
            data-testid="editor-subject"
            value={form.subject || ""}
            onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
            placeholder="Optional subject"
            className="input text-sm w-full"
          />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-[var(--ink-2)]">Body</label>
          <div className="text-[10px] text-[var(--ink-2)]">Use <code className="px-1 bg-[var(--bg-2)] rounded">%PLACEHOLDER%</code> for merge fields</div>
        </div>
        <textarea
          data-testid="editor-body"
          ref={bodyRef}
          value={form.body || ""}
          onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
          rows={18}
          className="input text-sm w-full font-mono-data"
        />
      </div>

      <div>
        <label className="text-xs text-[var(--ink-2)] block mb-1">Insert placeholder</label>
        <div className="flex flex-wrap gap-1">
          {Object.entries(options.placeholders || {}).map(([ph, desc]) => (
            <button
              key={ph}
              data-testid={`placeholder-${ph.replace(/%/g, "")}`}
              onClick={() => insertPlaceholder(ph)}
              title={desc}
              className="text-[10px] px-1.5 py-0.5 rounded border border-[var(--line)] bg-white hover:border-[var(--navy)] hover:text-[var(--navy)] transition font-mono-data"
            >{ph}</button>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-[var(--line)]">
        {onCancel && <button onClick={onCancel} className="btn-ghost text-sm !py-2 !px-3">Cancel</button>}
        <button data-testid="editor-save" onClick={() => onSave(form)} className="btn-primary flex items-center gap-1.5 text-sm !py-2 !px-3">
          <Save size={13} /> Save
        </button>
      </div>
    </div>
  );

  if (floating) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--line)]">
            <div className="font-medium text-sm">New template</div>
            <button onClick={onCancel} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={16} /></button>
          </div>
          {content}
        </div>
      </div>
    );
  }
  return content;
}

function PreviewModal({ preview, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--line)]">
          <div className="font-medium text-sm">Preview — {preview.name}</div>
          <button onClick={onClose} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={16} /></button>
        </div>
        <div className="p-5 text-sm">
          <div className="mb-2 text-xs text-[var(--ink-2)]">Rendered with sample data. Missing placeholders are highlighted below.</div>
          {preview.subject && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">Subject</div>
              <div className="font-medium">{preview.subject}</div>
            </div>
          )}
          <div className="whitespace-pre-wrap bg-[var(--bg-2)] p-4 rounded border border-[var(--line)]">
            {preview.body}
          </div>
          {preview.missing_placeholders?.length > 0 && (
            <div className="mt-3 text-xs text-[var(--err)]">
              Missing: {preview.missing_placeholders.map(p => <code key={p} className="mr-1 bg-[var(--err-bg)] px-1 rounded">{p}</code>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
